import * as React from "react"

interface VaultContextProps {
  // File id and collectionId
  selectedFileIds: Map<string, string>
  selectFile: (id: string, collectionId: string) => void
  selectFiles: (ids: [id: string, collectionId: string][]) => void
  unselectFile: (id: string) => void
  clearFileSelection: () => void
  searchInputRef: React.RefObject<HTMLInputElement | null> | null
}

const VaultContext = React.createContext<VaultContextProps>({
  selectedFileIds: new Map<string, string>(),
  selectFile: () => {},
  selectFiles: () => {},
  unselectFile: () => {},
  clearFileSelection: () => {},
  searchInputRef: null,
})

export const VaultContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [selectedFileIds, setSelectedIds] = React.useState(() => new Map<string, string>())
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const selectFile = React.useCallback((id: string, collectionId: string) => {
    setSelectedIds((prev) => new Map(prev).set(id, collectionId))
  }, [])

  const selectFiles = React.useCallback((ids: [id: string, collectionId: string][]) => {
    setSelectedIds(new Map(ids))
  }, [])

  const unselectFile = React.useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const clearFileSelection = React.useCallback(() => {
    setSelectedIds(() => new Map())
  }, [])

  const context = React.useMemo<VaultContextProps>(
    () => ({
      selectedFileIds,
      selectFile,
      selectFiles,
      unselectFile,
      clearFileSelection,
      searchInputRef,
    }),
    [selectedFileIds, selectFile, selectFiles, unselectFile, clearFileSelection]
  )

  return <VaultContext.Provider value={context}>{children}</VaultContext.Provider>
}

export function useVaultContext() {
  const context = React.useContext(VaultContext)
  return React.useMemo(() => context, [context])
}
